import { useApolloClient } from '@apollo/client';
import AWS from 'aws-sdk';
import { ManagedUpload } from 'aws-sdk/clients/s3';
import { AWSError } from 'aws-sdk/lib/error';
import moment from 'moment';

import { GetUploadCredentials } from '../graphql/athena/types/GetUploadCredentials';
import { GET_UPLOAD_CREDENTIALS } from '../graphql/mutations/cabinet/useGetUploadCredentials';

export const UPLOAD_CRED_KEY = 'UploadCredKey';
export interface Credentials {
  accessKeyId: string;
  secretAccessKey: string;
  sessionToken: string;
  expiration: string;
  baseUrl: string;
  bucket: string;
  pathPrefix: string;
}

export const useBulkUploadPhotosToS3 = () => {
  const client = useApolloClient();

  return [
    async (files: any[]) => {
      const storedCreds = localStorage.getItem(UPLOAD_CRED_KEY);
      let credentials: Credentials = storedCreds ? JSON.parse(storedCreds) : null;

      // 1. Renew credentials if needed
      if (!credentials || moment().isAfter(moment(credentials.expiration).subtract(5, 'minutes'))) {
        /** retrieve upload credentials */
        const { data, errors } = await client.mutate<GetUploadCredentials>({
          mutation: GET_UPLOAD_CREDENTIALS,
        });
        const creds = data?.cabinet.getUploadCredentials;

        if (errors || !creds || !creds.awsCredential) {
          throw new Error(`Error retrieving upload credentials: ${errors}`);
        }
        credentials = {
          accessKeyId: creds?.awsCredential?.accessKeyId || '',
          secretAccessKey: creds?.awsCredential?.secretAccessKey || '',
          sessionToken: creds?.awsCredential?.sessionToken || '',
          expiration: creds?.awsCredential?.expiration,
          baseUrl: creds?.baseUrl || '',
          bucket: creds?.bucket || '',
          pathPrefix: creds?.pathPrefix || '',
        };

        localStorage.setItem(UPLOAD_CRED_KEY, JSON.stringify(credentials));
      }

      const s3Client = new AWS.S3({
        accessKeyId: credentials.accessKeyId,
        secretAccessKey: credentials.secretAccessKey,
        sessionToken: credentials.sessionToken,
      });

      // 2. Upload document to S3
      const paths = await Promise.all(
        files.map((file) => {
          const objParams = {
            Bucket: credentials.bucket,
            Key: `${credentials.pathPrefix.substring(1)}${file.name}`,
            Body: file,
            ContentType: file.type,
          };

          return new Promise<string>((resolve, reject) => {
            s3Client
              .upload(objParams, { partSize: 50 * 1024 * 1024 })
              .send((error: AWSError | null, data: ManagedUpload.SendData) =>
                error ? reject(error) : resolve(data.Location),
              );
          });
        }),
      );

      return paths;
    },
  ];
};
